import * as React from "react";
import { cn } from "@/lib/utils";

export interface ProjectGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Search/filter input value
   */
  searchValue?: string;
  /**
   * Search input change handler
   */
  onSearchChange?: (value: string) => void;
  /**
   * Search placeholder text
   */
  searchPlaceholder?: string;
  /**
   * Grid items to display
   */
  children?: React.ReactNode;
  /**
   * Number of columns in the grid
   */
  columns?: 2 | 3 | 4 | 6;
}

const ProjectGrid = React.forwardRef<HTMLDivElement, ProjectGridProps>(
  (
    {
      searchValue = "",
      onSearchChange,
      searchPlaceholder = "Search projects...",
      children,
      columns = 3,
      className,
      ...props
    },
    ref
  ) => {
    const gridColsClass = {
      2: "grid-cols-1 md:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
      4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
      6: "grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
    }[columns];

    return (
      <div ref={ref} className={cn("w-full space-y-6", className)} {...props}>
        {/* Search Input Section */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
            <input
              type="search"
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex h-10 w-full rounded-lg border border-white/10 bg-[#1C1C1C] pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EDC29C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#161616] disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* Grid Section */}
        <div className={cn("grid gap-6", gridColsClass)}>{children}</div>
      </div>
    );
  }
);
ProjectGrid.displayName = "ProjectGrid";

export interface ProjectCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Project title
   */
  title: string;
  /**
   * Project description
   */
  description?: string;
  /**
   * Project icon or image
   */
  icon?: React.ReactNode;
  /**
   * Whether this is a "create new" card
   */
  isCreate?: boolean;
  /**
   * Click handler
   */
  onCardClick?: () => void;
}

const ProjectCard = React.forwardRef<HTMLDivElement, ProjectCardProps>(
  (
    {
      title,
      description,
      icon,
      isCreate = false,
      onCardClick,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        onClick={onCardClick}
        className={cn(
          "group relative rounded-xl border border-white/10 text-white shadow transition-all hover:shadow-md",
          onCardClick && "cursor-pointer hover:border-[#EDC29C]/40",
          isCreate &&
            "border-dashed border-2 hover:border-[#EDC29C] hover:bg-[#EDC29C]/5",
          className
        )}
        style={{
          backgroundColor: "rgba(235, 220, 207, 0.045)",
          backdropFilter: "blur(1000px)",
          WebkitBackdropFilter: "blur(1000px)",
        }}
        {...props}
      >
        <div className="p-6 space-y-4">
          {/* Icon Section */}
          {icon && (
            <div
              className={cn(
                "flex h-12 w-12 items-center justify-center rounded-lg",
                isCreate
                  ? "bg-[#EDC29C]/10 text-[#EDC29C]"
                  : "bg-[#2C2C2C] text-white/60"
              )}
            >
              {icon}
            </div>
          )}

          {/* Content Section */}
          <div className="space-y-2">
            <h3
              className={cn(
                "font-semibold leading-none tracking-tight text-[#FAF1E9]",
                isCreate && "text-[#EDC29C]"
              )}
            >
              {title}
            </h3>
            {description && (
              <p className="text-sm text-[#FAF1E9]/70 line-clamp-2">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
);
ProjectCard.displayName = "ProjectCard";

export { ProjectGrid, ProjectCard };
